import React from 'react';
import { X, GitBranch, ArrowRight, ArrowLeft, Layers, Hash, AlertTriangle } from 'lucide-react';
import { LdgNodeData } from './useLdgGraphData';
interface NodeDetailPanelProps {
  nodeData: LdgNodeData | null;
  onClose: () => void;
  onSelectNode: (nodeId: string) => void;
}
export function NodeDetailPanel({
  nodeData,
  onClose,
  onSelectNode
}: NodeDetailPanelProps) {
  if (!nodeData) return null;
  const {
    colorSet
  } = nodeData;
  return <>
      {/* Mobile: Bottom sheet */}
      <div className="md:hidden absolute inset-x-0 bottom-0 max-h-[60vh] bg-white rounded-t-2xl shadow-2xl border-t border-slate-200 flex flex-col z-50 overflow-hidden" data-id="element-5891" data-mp="4yrlhgsyf3o6">
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-1 flex-shrink-0" data-id="element-5892" data-mp="3sasoj2m61sno">
          <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-5893" data-mp="3deba22iojxww" />
        </div>

        {/* Header */}
        <div className={`px-4 py-3 border-b border-slate-100 flex items-start justify-between ${colorSet.light}`} data-id="element-5894" data-mp="3idefvp6hi85x">
          <div className="min-w-0 flex-1" data-id="element-5895" data-mp="r6y2rtlzw7pz">
            <div className="flex items-center gap-2 mb-0.5" data-id="element-5896" data-mp="ra3nkwnlcnfw">
              <div className={`w-2 h-2 rounded-full ${colorSet.bg}`} data-id="element-5897" data-mp="m84ldq3l7wkj" />
              <span className={`text-[10px] font-bold uppercase tracking-wider ${colorSet.text}`} data-id="element-5898" data-mp="10979e8e57gy3">
                {nodeData.module}
              </span>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${colorSet.bg} text-white`} data-id="element-5899" data-mp="8h9lv4ezartm">
                {nodeData.levelCode}
              </span>
            </div>
            <h3 className="font-mono font-bold text-slate-900 text-base leading-tight truncate" data-id="element-5900" data-mp="hacw73ubuok1">
              {nodeData.label}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white/50 rounded-lg transition-colors flex-shrink-0 ml-2" data-id="element-5901" data-mp="ch076slqkfqr">
            <X className="w-5 h-5" data-id="element-5902" data-mp="3gx2s8qflimwy" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-5903" data-mp="1jwezts75hz0g">
          {!nodeData.hasContent && <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2" data-id="element-5904" data-mp="1otyrpuwruaav">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-5905" data-mp="1tol2pwyxy0uf" />
              <p className="text-xs text-amber-700" data-id="element-5906" data-mp="1yktd6x2715v9">
                Geen content — phantom node.
              </p>
            </div>}

          {/* Compact properties */}
          <div className="flex items-center gap-3 text-xs" data-id="element-5907" data-mp="15xp4oi3w69eq">
            <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100" data-id="element-5908" data-mp="1awa3qe7eqn1l">
              <Layers className="w-3 h-3 text-slate-400" data-id="element-5909" data-mp="1fvdbi6erv7d9" />
              <span className="font-bold text-slate-700" data-id="element-5910" data-mp="1fsqs99iir3f7">
                Tier {nodeData.tier}
              </span>
            </div>
            <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100" data-id="element-5911" data-mp="2r9qt4tixmqku">
              <Hash className="w-3 h-3 text-slate-400" data-id="element-5912" data-mp="flvfo94p623t" />
              <span className="text-slate-600 truncate max-w-[120px]" data-id="element-5913" data-mp="foq389j0c31g">
                {nodeData.skill}
              </span>
            </div>
          </div>

          {/* Prerequisites */}
          {nodeData.prerequisites.length > 0 && <div data-id="element-5914" data-mp="wzaqkxxt57f">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-5915" data-mp="xhfy72kutrh">
                <ArrowRight className="w-3 h-3" data-id="element-5916" data-mp="3s27fftmuvbbg" />
                Vereist ({nodeData.prerequisites.length})
              </h4>
              <div className="flex flex-wrap gap-1.5" data-id="element-5917" data-mp="3j3dl69wzyx12">
                {nodeData.prerequisites.map(prereq => <button key={prereq} onClick={() => onSelectNode(prereq)} className="font-mono text-[11px] text-slate-600 bg-slate-50 px-2 py-1 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors" data-id="element-5918" data-mp="3e5c41uj4t7s8">
                    {prereq}
                  </button>)}
              </div>
            </div>}

          {/* Dependents */}
          {nodeData.dependents.length > 0 && <div data-id="element-5919" data-mp="sg2o7gviqq">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-5920" data-mp="1bz688usd6c">
                <GitBranch className="w-3 h-3" data-id="element-5921" data-mp="3mh6fs0bmks1t" />
                Ontsluit ({nodeData.dependents.length})
              </h4>
              <div className="flex flex-wrap gap-1.5" data-id="element-5922" data-mp="3r8eqczooyjgk">
                {nodeData.dependents.map(dep => <button key={dep} onClick={() => onSelectNode(dep)} className="font-mono text-[11px] text-slate-600 bg-slate-50 px-2 py-1 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors" data-id="element-5923" data-mp="3dh9n7nm4791h">
                    {dep}
                  </button>)}
              </div>
            </div>}

          {nodeData.prerequisites.length === 0 && nodeData.dependents.length === 0 && <p className="text-xs text-slate-500 italic" data-id="element-5924" data-mp="2kvtb63snfi92">
                Geïsoleerde node (geen verbindingen).
              </p>}
        </div>
      </div>

      {/* Desktop: Right side panel */}
      <div className="hidden md:flex absolute top-4 right-4 bottom-4 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 flex-col z-50 overflow-hidden" data-id="element-5925" data-mp="2twrjgya4ebxg">
        {/* Header */}
        <div className={`p-4 border-b border-slate-100 flex items-start justify-between ${colorSet.light}`} data-id="element-5926" data-mp="33sm6e5ldmt61">
          <div data-id="element-5927" data-mp="3cl67rdt1zbuk">
            <div className="flex items-center gap-2 mb-1" data-id="element-5928" data-mp="3cko3btw8lrbk">
              <div className={`w-2.5 h-2.5 rounded-full ${colorSet.bg}`} data-id="element-5929" data-mp="3cn992tznorel" />
              <span className={`text-xs font-bold uppercase tracking-wider ${colorSet.text}`} data-id="element-5930" data-mp="3rj8f7z37ghh3">
                {nodeData.module}
              </span>
            </div>
            <h3 className="font-mono font-bold text-slate-900 text-lg leading-tight" data-id="element-5931" data-mp="3tnv70a4tsxqr">
              {nodeData.label}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white/50 rounded-lg transition-colors" data-id="element-5932" data-mp="7fc6fwej0rf8">
            <X className="w-5 h-5" data-id="element-5933" data-mp="c85a0gy9dtpq" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6" data-id="element-5934" data-mp="h4vqh5dz6rbo">
          {!nodeData.hasContent && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2.5" data-id="element-5935" data-mp="l4xjyr9k4efv">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-5936" data-mp="q3qtk8lq3lar" />
              <div data-id="element-5937" data-mp="v454byepl9ch">
                <h4 className="text-sm font-bold text-amber-800" data-id="element-5938" data-mp="v2ka69wb3sf5">
                  Geen content
                </h4>
                <p className="text-xs text-amber-700 mt-0.5" data-id="element-5939" data-mp="2ww1d509hs53k">
                  Deze family heeft nog geen vragen in de database. Het is een
                  "phantom node" in de graaf.
                </p>
              </div>
            </div>}

          {/* Properties */}
          <div className="grid grid-cols-2 gap-3" data-id="element-5940" data-mp="2fmyu4bkem1cw">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-5941" data-mp="2fnqstv9asf7e">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5" data-id="element-5942" data-mp="2aonmm7hl02xu">
                <Layers className="w-3 h-3" data-id="element-5943" data-mp="38b4hl62ue4gf" />
                Tier
              </div>
              <div className="font-bold text-slate-900 text-lg" data-id="element-5944" data-mp="33duu3vz0k2v8">
                {nodeData.tier}
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-5945" data-mp="2ybvsaqix9e0e">
                Niveau {nodeData.levelCode}
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-5946" data-mp="1my1ll05zvixc">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5" data-id="element-5947" data-mp="1hz6w5fdiuw49">
                <Hash className="w-3 h-3" data-id="element-5948" data-mp="27mt1jic94sek" />
                Skill
              </div>
              <div className="font-medium text-slate-900 text-sm truncate" title={nodeData.skill} data-id="element-5949" data-mp="2cm5rwd2c0hlw">
                {nodeData.skill}
              </div>
              <div className="text-xs text-slate-500 truncate" title={nodeData.subSkill} data-id="element-5950" data-mp="22nzo1pwyt1ee">
                {nodeData.subSkill}
              </div>
            </div>
          </div>

          {/* Prerequisites */}
          <div data-id="element-5951" data-mp="2vc5aqi7kx0gv">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-5952" data-mp="2veriahugzfz3">
              <ArrowRight className="w-4 h-4 text-slate-300" data-id="element-5953" data-mp="2ln3t6nldyb0s" />
              Vereist ({nodeData.prerequisites.length})
            </h4>
            {nodeData.prerequisites.length === 0 ? <div className="text-sm text-slate-500 italic bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-5954" data-mp="3e3eleut35va1">
                Dit is een root node (geen vereisten).
              </div> : <div className="space-y-2" data-id="element-5955" data-mp="xpck4cs5xqbt">
                {nodeData.prerequisites.map(prereq => <button key={prereq} onClick={() => onSelectNode(prereq)} className="w-full text-left p-2.5 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors group flex items-center justify-between" data-id="element-5956" data-mp="1cdzb5br5k6iv">
                    <span className="font-mono text-sm text-slate-700 group-hover:text-indigo-700 transition-colors truncate" data-id="element-5957" data-mp="erjta8utd6us">
                      {prereq}
                    </span>
                    <ArrowLeft className="w-4 h-4 text-slate-300 group-hover:text-indigo-400" data-id="element-5958" data-mp="oo4yau6u2hym" />
                  </button>)}
              </div>}
          </div>

          {/* Dependents */}
          <div data-id="element-5959" data-mp="202rzon5g3c3g">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-5960" data-mp="203bgg0jczufw">
              <GitBranch className="w-4 h-4 text-slate-300" data-id="element-5961" data-mp="18twioau59itx" />
              Ontsluit ({nodeData.dependents.length})
            </h4>
            {nodeData.dependents.length === 0 ? <div className="text-sm text-slate-500 italic bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-5962" data-mp="z0fq1a5qu9ru">
                Dit is een leaf node (ontsluit niets).
              </div> : <div className="space-y-2" data-id="element-5963" data-mp="p55lofrvhqaw">
                {nodeData.dependents.map(dep => <button key={dep} onClick={() => onSelectNode(dep)} className="w-full text-left p-2.5 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors group flex items-center justify-between" data-id="element-5964" data-mp="l0xv0d1uu0a1">
                    <span className="font-mono text-sm text-slate-700 group-hover:text-indigo-700 transition-colors truncate" data-id="element-5965" data-mp="2aeta03xdm52l">
                      {dep}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-400" data-id="element-5966" data-mp="2d1btki49ztt1" />
                  </button>)}
              </div>}
          </div>
        </div>
      </div>
    </>;
}